import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Google as GoogleIcon, SelfImprovement as MeditationIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { trackUserLogin } from '../services/analytics';

const LoginScreen: React.FC = () => {
  const { signInWithGoogle } = useAuth();
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const signInTimeoutRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (signInTimeoutRef.current) {
        window.clearTimeout(signInTimeoutRef.current);
      }
    };
  }, []);

  const handleSignIn = async () => {
    const requestId = ++requestIdRef.current;
    setSignInError(null);
    setSigningIn(true);

    if (signInTimeoutRef.current) {
      window.clearTimeout(signInTimeoutRef.current);
    }
    signInTimeoutRef.current = window.setTimeout(() => {
      if (requestId === requestIdRef.current) {
        setSigningIn(false);
        setSignInError('Sign-in took too long. Please try again.');
      }
    }, 35000);

    try {
      await signInWithGoogle();
      trackUserLogin('google');
    } catch (error) {
      console.error('Sign in failed:', error);
      const message = error instanceof Error ? error.message : 'Google sign-in failed. Please try again.';
      if (requestId === requestIdRef.current) {
        setSignInError(message);
      }
    } finally {
      if (signInTimeoutRef.current) {
        window.clearTimeout(signInTimeoutRef.current);
      }
      if (requestId === requestIdRef.current) {
        setSigningIn(false);
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 6,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            width: '100%',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <MeditationIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h3" component="h1" gutterBottom>
              Lumina
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
              Your Personal Meditation Pal
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Sign in to access personalized meditation sessions, track your progress, 
            and discover your inner peace with AI-guided mindfulness experiences.
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={signingIn ? <CircularProgress size={18} sx={{ color: '#667eea' }} /> : <GoogleIcon />}
            onClick={handleSignIn}
            disabled={signingIn}
            sx={{
              backgroundColor: 'white',
              color: '#667eea',
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {signingIn ? 'Signing in...' : 'Continue with Google'}
          </Button>

          {signInError && (
            <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
              {signInError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 4, opacity: 0.8 }}>
            <Typography variant="body2">
              ✨ Personalized meditation sessions
            </Typography>
            <Typography variant="body2">
              📊 Track your mindfulness journey
            </Typography>
            <Typography variant="body2">
              🎵 AI-generated guided experiences
            </Typography>
            <Typography variant="body2">
              🔒 Secure and private
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginScreen;
